import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { StoreDetailComponent } from '@components/stores-locator/components/store-detail';
import { IsStoreVisible } from '@components/stores-locator/components/store-list/pipe';
import { StoresService } from '@components/stores-locator/services';
import { ElasticSearchService } from '@services/elastic-search';
import { LocalitySearch } from '@services/locality-search/models';
import { LogsService } from '@services/logs';
import { StoreLocatorService } from '@services/store-locator';
import { ButtonComponent } from '@shared/components/button';
import { MapService } from '@shared/components/map/services';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { debounceTime, EMPTY, of, Subject, take } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-store-list',
  imports: [TitleComponent, ButtonComponent, StoreDetailComponent, AsyncPipe, IsStoreVisible],
  templateUrl: './store-list.component.html',
  styleUrl: './store-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoreListComponent {
  readonly inputLocationElement = viewChild.required<ElementRef<HTMLInputElement>>('inputLocationElement');
  readonly storeListElement = viewChild.required<ElementRef<HTMLElement>>('storesList');
  title: Title = {
    color: 'BLACK',
    text: 'Trouver une boutique',
    weight: 700,
    font: 'INTER',
    level: 'SPAN',
    size: {
      desktop: 'H6',
      mobile: 'H5',
      custom: {
        mobile: '2rem',
        desktop: '2.4rem',
      },
    },
  };
  error = signal<string>(null!);
  isStoresLoaded = signal(true);
  isGeolocaliseLoading = signal(false);
  locationSuggestions = signal<LocalitySearch[]>([]);
  showDropdown = signal(false);

  readonly #storesService = inject(StoresService);
  readonly stores$ = this.#storesService.getStores$();
  readonly urlAppointment$ = this.#storesService.getUrlAppointment$();
  readonly storesVisible$ = this.#storesService.getStoresVisible$().pipe(
    tap(() => {
      this.scrollToTopList();
    })
  );
  readonly #elasticSearchService = inject(ElasticSearchService);
  readonly #mapService = inject(MapService);
  readonly #storeLocatorService = inject(StoreLocatorService);
  readonly #logsService = inject(LogsService);
  readonly #searchInputValue: Subject<string> = new Subject();

  constructor() {
    this.#searchInputValue
      .pipe(
        debounceTime(300),
        switchMap((value: string) => {
          if (!value || value.length < 3) {
            this.locationSuggestions.set([]);
            this.showDropdown.set(false);
            return of(null);
          }
          return this.#storeLocatorService.getLocation(value);
        })
      )
      .subscribe((locations) => {
        if (locations && locations.length > 0) {
          this.locationSuggestions.set(locations);
          this.showDropdown.set(true);
        } else {
          this.locationSuggestions.set([]);
          this.showDropdown.set(false);
        }
      });
  }

  onInputChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.#searchInputValue.next(value);
  }

  onInputKeydown(event: KeyboardEvent): void {
    const suggestions = this.locationSuggestions();

    if (event.key === 'Enter') {
      event.preventDefault();
      if (suggestions.length > 0 && this.showDropdown()) {
        this.selectLocation(suggestions[0]!);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.showDropdown.set(false);
    }
  }

  selectLocation(location: LocalitySearch): void {
    const input = this.inputLocationElement().nativeElement;
    input.value = location.name;
    this.showDropdown.set(false);
    this.locationSuggestions.set([]);

    this.#logsService.debug('StoreListComponent', 'Selected location:', location);

    this.#storeLocatorService.resolveCoordinates(location).subscribe((coordinates) => {
      if (coordinates?.lat === undefined || coordinates.lng === undefined) {
        this.#logsService.warn('StoreListComponent', 'No coordinates resolved for the selected location');
        return;
      }
      this.#logsService.debug('StoreListComponent', 'Resolved coordinates:', coordinates);
      this.getStores(coordinates.lat, coordinates.lng);
    });
  }

  onInputBlur(): void {
    setTimeout(() => {
      this.showDropdown.set(false);
    }, 200);
  }

  onInputFocus(): void {
    const input = this.inputLocationElement().nativeElement;
    if (input.value.length >= 3 && this.locationSuggestions().length > 0) {
      this.showDropdown.set(true);
    }
  }

  onGeolocalise(): void {
    if (this.isGeolocaliseLoading()) return;
    this.isGeolocaliseLoading.set(true);
    this.#mapService
      .getPosition$()
      .pipe(
        take(1),
        tap((value) => {
          if (value.status === 'OK') {
            this.inputLocationElement().nativeElement.value = value.address.locality;
            this.getStores(value.lat, value.lng);
          } else if (value.status === 'KO') {
            this.error.set(value.error);
          }
          this.isGeolocaliseLoading.set(false);
        }),
        catchError((error: GeolocationPositionError) => {
          this.#logsService.debug('StoreListComponent', 'Geolocation failed', {
            code: error.code,
            message: error.message,
          });
          this.isGeolocaliseLoading.set(false);
          this.error.set('Vous devez activer la localisation pour pouvoir être localisé.');
          return EMPTY;
        })
      )
      .subscribe();
  }

  private scrollToTopList(): void {
    const list = this.storeListElement()?.nativeElement;
    if (list) list.scrollTop = 0;
  }

  private getStores(lat: number, lon: number): void {
    this.#logsService.debug('StoreListComponent', 'Searching stores at coordinates:', { lat, lon });
    this.isStoresLoaded.set(false);

    this.#elasticSearchService
      .getNearestStores({ lat, lon })
      .pipe(
        take(1),
        catchError((err: unknown) => {
          // do not log lat/lon: the searched point can be the visitor's geolocation
          this.#logsService.error('StoreListComponent', 'Nearest stores search failed', err);
          return of([]);
        }),
        tap((finalStores) => {
          this.#storesService.setStores(finalStores);
          this.isStoresLoaded.set(true);
        })
      )
      .subscribe();
  }
}
